/**
 * AI Crosstabs (DANI) layout storage — M4S-5579.
 *
 * AI Crosstabs state lives in its own `crosstab_layout.ai_layout` JSONB column instead of being
 * smuggled through the legacy tool's `layout.config` as a single-cell grid. The legacy tool owns
 * `layout` and never reads `ai_layout`; the AI tool owns `ai_layout` and never writes `layout`.
 * That removes the shared-column hazard where a row opened by the wrong tool could be rewritten
 * in that tool's shape.
 *
 * `ai_layout` is the only location this module reads. Converting the pre-migration rows — where
 * state sat at `layout.config[0][0]` under `version: 'dani-1'` — is a one-time database
 * operation (`migrations/studio/backfill-crosstab-ai-layout.sql`), deliberately NOT a compatibility path
 * carried in the application. That backfill must run BEFORE this code is deployed; the copy is
 * invisible to the running app because it leaves `layout` untouched. See the ordering in that
 * script's header.
 */

import { parseEmptyAnswers, type EmptyAnswersRecord } from './empty-answers';
import { parseDaniOrigin, type DaniOrigin } from './dani/dani-origin';
import type {
    FilterTabs,
    LiveFilter,
    WeightScheme,
} from '@/app/(private)/ai/components/Crosstabs/crosstab-types';

import { parseAnswerEdits, type AnswerEditsMap } from './answer-edits';
import { parseBoxChoices, type BoxChoiceMap } from './box-rows';
import { parseConversion, type ConversionRecord } from './classic-conversion';
import { parseExportPresetId } from './export-presets';
import { parseFilterTabs, parseLiveFilters } from './live-filters';
import { parsePrivacyFloor } from './small-cell-privacy';
import { parseMatrixEdits, type MatrixEditsMap } from './matrix-edits';
import { pruneByColumnOverrides } from './matrix-orientation';
import { parseSurveyScope, type SurveyScope } from './saved-survey-scope';
import { parseStatSettings, type StatSettings } from './stat-settings';
import { parseRespondentSet, respondentSetOf, type RespondentSet } from './respondent-set';
import { parseStudyTypeRecord, type StudyTypeRecord } from './study-type';
import {
    activeScheme,
    parseWeightScheme,
    parseWeightSchemeSet,
    parseWeightingRecord,
    type WeightingRecord,
} from './weight-scheme';

/**
 * A saved response filter carried on an AI crosstab: the workspace's `LiveFilter`, parsed by
 * `parseLiveFilters` on the write as on the read (M4S-5777 X39), so a request body or a column
 * can never hand the workspace a filter it cannot read.
 */
export type AiCrosstabLiveFilter = LiveFilter;

/** The AI workspace state persisted with a crosstab. */
export interface AiCrosstabState {
    activeRows?: string[];
    activeBanner?: string[];
    bannerRenames?: Record<string, string>;
    liveFilters?: AiCrosstabLiveFilter[];
    customVariables?: unknown[];
    /**
     * Per-question matrix orientation, keyed by upper-cased qid: true = By Column (M4S-5595 item 4
     * criterion 5; M4S-5618). Holds only differences from `matrixByColumn`. A key inside this
     * existing JSONB document — not a schema change.
     */
    perQuestionMatrixTranspose?: Record<string, boolean>;
    /** Crosstab-wide matrix orientation (M4S-5618): true = By Column. Stored only when true. */
    matrixByColumn?: boolean;
    /**
     * Option edits on matrix questions (M4S-5618), keyed by upper-cased qid and naming statements
     * and answer choices by survey code (`src/lib/crosstab/matrix-edits.ts`). Stored only when
     * non-empty, and parsed on the write as on the read: a POST body is as untrusted as the column.
     */
    matrixEdits?: MatrixEditsMap;
    /**
     * M4S-5777 (X59): matrices in the banner follow their orientation and option edits. Stored only
     * when true; every workspace save from that release on sends it on a crosstab that has it, and a
     * new crosstab has it. Absent: a banner matrix draws By Row as surveyed (`matrix-banner.ts`).
     */
    matrixBannerViews?: boolean;
    /**
     * The crosstab-wide Top 2 Box, Bottom 2 Box and NPS switches (M4S-5607). Each is stored only
     * when true, so an absent key reads as off, and typed `?: boolean` like `matrixByColumn`
     * because `withAiStateDefaults` returns `Required<AiCrosstabState>`.
     */
    showTopBox?: boolean;
    showBottomBox?: boolean;
    showNPS?: boolean;
    /**
     * The per-question Top 2 / Bottom 2 / NPS choices and Reverse scale (M4S-5607), keyed by
     * upper-cased qid and holding only differences from the switches above. Stored only when
     * non-empty, and parsed on the write as on the read: a POST body is as untrusted as the column.
     */
    perQuestionT2B2?: BoxChoiceMap;
    /**
     * The surveys this crosstab was built on, stamped with the key of the record that saved them
     * (M4S-5646). Record state, not a setting: it is stored only for a crosstab on something other
     * than its whole project, read by the data route on the first `layout=` load, and never handed
     * to the workspace (`withAiStateDefaults` leaves it out; the routes answer `savedSurveyIds`).
     * Parsed on the write as on the read. Spec: docs/pdlc/crosstabs-saved-survey-scope.md §6.
     */
    surveyScope?: SurveyScope;
    /**
     * BT-2084: whether the crosstab is weighted, stored only when true, and its weight scheme
     * (who is weighted, and to what), stored only when it parses (`parseWeightScheme`). Record
     * state, so everyone who opens or exports the crosstab sees the same weighted numbers.
     */
    weighted?: boolean;
    weightScheme?: WeightScheme | null;
    /**
     * M4S-5777 (X83, X90): every weighting scheme the crosstab holds, which one its tables use, and
     * whether the unweighted figures show beside the weighted. One versioned block, parsed by
     * `parseWeightingRecord` on the write as on the read. `weightScheme` above stays, written as
     * the active scheme alone, so a build from before X83 reopening the record weighs it the same;
     * a record without the block is read by lifting `weightScheme` (`parseWeightSchemeSet`).
     */
    weighting?: WeightingRecord | null;
    /**
     * M4S-5777 X14: the statistics settings (significance levels, test, comparison, markers, Mean
     * and Median, decimals, low-base thresholds, completes only), one versioned block parsed by
     * `parseStatSettings` on the write as on the read. Absent on a crosstab saved before X14: the
     * workspace then shows what the opening browser remembered, until the first edit stamps it.
     */
    statSettings?: StatSettings | null;
    /**
     * M4S-5777: this crosstab names a coded open-end net with no code of its own by its own id
     * (`oeNetHeadId`), not by the code it borrows from its first member. Every workspace save from
     * that release on sends it, stored only when true. A record without it is read through
     * `migrateBorrowedOeIds` (option-codes.ts). Docs: docs/pdlc/crosstabs-open-ends-tpbp.md §4.1.
     */
    oeNetIds?: boolean;
    /**
     * M4S-5777 (K3): answers of ordinary questions renamed, hidden or moved, keyed by upper-cased
     * qid and naming answers by survey code (`src/lib/crosstab/answer-edits.ts`). Stored only when
     * non-empty, and parsed on the write as on the read, like `matrixEdits`.
     */
    answerEdits?: AnswerEditsMap;
    /**
     * M4S-5777 (K5): what the classic conversion that made this crosstab could not bring, shown on
     * reopen. Record state, not a setting. Parsed on the write as on the read.
     */
    conversion?: ConversionRecord | null;
    /**
     * M4S-5777 (X22): the data set this crosstab is built on (`respondent-set.ts`). Stored only
     * when it is not completes, so every crosstab saved before reads as completes. Parsed on the
     * write as on the read; the data route reads it on an open's first load.
     */
    respondentSet?: RespondentSet;
    /**
     * M4S-5777 X7, X8, X67: the crosstab's study type, the setup it started from and the statistics
     * standard it recorded (`src/lib/crosstab/study-type.ts`). Record state, not a setting. Absent
     * on a crosstab saved before X7: the workspace then freezes today's guess for the session and
     * its first edit saves it. Parsed on the write as on the read.
     */
    studyType?: StudyTypeRecord | null;
    /**
     * M4S-5777 X41: the question, survey or variable the crosstab flips through, one tab per answer.
     * A record setting (`filterTabs`). Stored only when set, parsed on the write as on the read.
     */
    filterTabs?: FilterTabs | null;
    /**
     * M4S-5777 (X105): the export preset the crosstab was last exported with (`export-presets.ts`).
     * A record setting (`exportPreset`). Stored only when set, parsed on the write as on the read.
     */
    exportPreset?: string | null;
    /**
     * M4S-5777 (X135): this crosstab's minimum group size, which raises the account's and never
     * lowers it (`effectivePrivacyFloor`, small-cell-privacy.ts). Stored only when set.
     */
    privacyFloor?: number | null;
    /**
     * M4S-5777 (X101): answers nobody chose, hidden crosstab-wide and per question
     * (`src/lib/crosstab/empty-answers.ts`). Stored only when it hides something, parsed on the
     * write as on the read.
     */
    emptyAnswers?: EmptyAnswersRecord | null;
    /**
     * M4S-5777 X150 (L2): the DANI chat this crosstab came from, stamped with the record's own key
     * (`dani-origin.ts`). Browser-carried and informational; the save route keeps it only under
     * that key. Parsed on the write as on the read.
     */
    daniOrigin?: DaniOrigin | null;
}

/** The `ai_layout` column's contents. */
export interface AiCrosstabLayout {
    version: string;
    settings: Record<string, unknown>;
    state: AiCrosstabState;
}

/** Version stamped on state written to `ai_layout`. */
export const AI_LAYOUT_VERSION = 'dani-2';

const DEFAULT_AI_SETTINGS: Record<string, unknown> = {
    withCounts: false,
    withPercentages: true,
    showColumnTotals: false,
    showRowTotals: false,
    showQuestionIdInTabs: false,
    decimalsShown: 1,
    showMean: false,
    statTesting: '0',
};

/** A crosstab record as returned by the portal API. */
export interface CrosstabLayoutRecord {
    aiLayout?: unknown;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** Builds the `ai_layout` payload for a save. Empty collections are omitted, not stored as `[]`. */
export function buildAiLayout(
    state: AiCrosstabState,
    settings?: Record<string, unknown>,
): AiCrosstabLayout {
    const matrixEdits = parseMatrixEdits(state.matrixEdits);
    const perQuestionT2B2 = parseBoxChoices(state.perQuestionT2B2);
    const surveyScope = parseSurveyScope(state.surveyScope);
    const weighting = parseWeightingRecord(state.weighting);
    // M4S-5777 (X83): with the block, `weightScheme` is its active scheme and nothing else, so the
    // two can never disagree in a stored record.
    const weightScheme = weighting
        ? parseWeightScheme(activeScheme(parseWeightSchemeSet(weighting)))
        : parseWeightScheme(state.weightScheme);
    const statSettings = parseStatSettings(state.statSettings);
    const answerEdits = parseAnswerEdits(state.answerEdits);
    const conversion = parseConversion(state.conversion);
    const respondentSet = parseRespondentSet(state.respondentSet);
    const studyType = parseStudyTypeRecord(state.studyType);
    const liveFilters = parseLiveFilters(state.liveFilters);
    const filterTabs = parseFilterTabs(state.filterTabs);
    const exportPreset = parseExportPresetId(state.exportPreset);
    const privacyFloor = parsePrivacyFloor(state.privacyFloor);
    const emptyAnswers = parseEmptyAnswers(state.emptyAnswers);
    const daniOrigin = parseDaniOrigin(state.daniOrigin);
    return {
        version: AI_LAYOUT_VERSION,
        settings: settings ?? DEFAULT_AI_SETTINGS,
        state: {
            activeRows: state.activeRows ?? [],
            activeBanner: state.activeBanner ?? [],
            ...(state.bannerRenames && Object.keys(state.bannerRenames).length
                ? { bannerRenames: state.bannerRenames }
                : {}),
            ...(liveFilters.length ? { liveFilters } : {}),
            ...(state.customVariables?.length ? { customVariables: state.customVariables } : {}),
            ...(state.perQuestionMatrixTranspose &&
            Object.keys(state.perQuestionMatrixTranspose).length
                ? { perQuestionMatrixTranspose: state.perQuestionMatrixTranspose }
                : {}),
            ...(state.matrixByColumn === true ? { matrixByColumn: true } : {}),
            ...(Object.keys(matrixEdits).length ? { matrixEdits } : {}),
            ...(state.matrixBannerViews === true ? { matrixBannerViews: true } : {}),
            ...(state.showTopBox === true ? { showTopBox: true } : {}),
            ...(state.showBottomBox === true ? { showBottomBox: true } : {}),
            ...(state.showNPS === true ? { showNPS: true } : {}),
            ...(Object.keys(perQuestionT2B2).length ? { perQuestionT2B2 } : {}),
            ...(surveyScope ? { surveyScope } : {}),
            ...(state.weighted === true ? { weighted: true } : {}),
            ...(weightScheme ? { weightScheme } : {}),
            ...(weighting ? { weighting } : {}),
            ...(statSettings ? { statSettings } : {}),
            ...(state.oeNetIds === true ? { oeNetIds: true } : {}),
            ...(Object.keys(answerEdits).length ? { answerEdits } : {}),
            ...(conversion ? { conversion } : {}),
            ...(respondentSet && respondentSet !== 'completes' ? { respondentSet } : {}),
            ...(studyType ? { studyType } : {}),
            ...(filterTabs ? { filterTabs } : {}),
            ...(exportPreset ? { exportPreset } : {}),
            ...(privacyFloor ? { privacyFloor } : {}),
            ...(emptyAnswers ? { emptyAnswers } : {}),
            ...(daniOrigin ? { daniOrigin } : {}),
        },
    };
}

/**
 * Reads AI state from `ai_layout`.
 *
 * Returns `null` for a row that carries no AI state — a legacy crosstab, or an AI row the
 * backfill has not converted. A caller must treat `null` as "not an AI crosstab" rather than
 * "an empty AI crosstab", otherwise opening a legacy row in the AI workspace would offer to
 * save an empty state over it.
 */
export function readAiCrosstabState(record: CrosstabLayoutRecord): AiCrosstabState | null {
    const { aiLayout } = record;

    if (isPlainObject(aiLayout) && isPlainObject(aiLayout.state)) {
        return aiLayout.state as AiCrosstabState;
    }

    return null;
}

/** Whether a record belongs to the AI tool. */
export function hasAiCrosstabState(record: CrosstabLayoutRecord): boolean {
    return readAiCrosstabState(record) !== null;
}

/**
 * Normalises a read state into fully-populated fields for the workspace.
 *
 * The matrix map is pruned against the record's default (M4S-5618): every Edit Row Variable save
 * used to store an entry, `false` included, and under the resolution order those would pin a
 * question By Row out of the crosstab default's reach. A record from before `matrixByColumn`
 * existed defaults to false, so dropping its `false` entries changes nothing it draws.
 *
 * The matrix option edits are parsed into their one canonical shape. JSONB hands the object keys
 * back ordered by length, and the workspace compares settings as JSON, so an unparsed record would
 * read as changed against its own Save. The per-question Top 2 / Bottom 2 map (M4S-5607) is parsed
 * for the same reason, and the three switches read as off unless the record says `true`.
 *
 * The saved survey scope (M4S-5646) is left out: it is the data route's to read, and the workspace
 * gets the record's own list as `savedSurveyIds` instead.
 */
export function withAiStateDefaults(
    state: AiCrosstabState | null,
): Required<Omit<AiCrosstabState, 'surveyScope'>> {
    const matrixByColumn = state?.matrixByColumn === true;
    return {
        activeRows: state?.activeRows ?? [],
        activeBanner: state?.activeBanner ?? [],
        bannerRenames: state?.bannerRenames ?? {},
        liveFilters: parseLiveFilters(state?.liveFilters),
        customVariables: state?.customVariables ?? [],
        perQuestionMatrixTranspose: pruneByColumnOverrides(
            state?.perQuestionMatrixTranspose,
            matrixByColumn,
        ),
        matrixByColumn,
        matrixEdits: parseMatrixEdits(state?.matrixEdits),
        // M4S-5777 (X59): absent means saved before banner matrices followed their view.
        matrixBannerViews: state?.matrixBannerViews === true,
        showTopBox: state?.showTopBox === true,
        showBottomBox: state?.showBottomBox === true,
        showNPS: state?.showNPS === true,
        perQuestionT2B2: parseBoxChoices(state?.perQuestionT2B2),
        // BT-2084: absent reads as unweighted, whatever a browser remembers.
        weighted: state?.weighted === true,
        weightScheme: parseWeightScheme(state?.weightScheme),
        // M4S-5777 (X83): null is a crosstab saved before several schemes; the workspace lifts its
        // `weightScheme` (`parseWeightSchemeSet`).
        weighting: parseWeightingRecord(state?.weighting) ?? null,
        // M4S-5777 X14: null is a crosstab saved before X14, not a default; the workspace tells
        // the two apart (`resolveStatSettings`).
        statSettings: parseStatSettings(state?.statSettings),
        // M4S-5777: absent means saved before open-end nets had their own ids.
        oeNetIds: state?.oeNetIds === true,
        // M4S-5777: parsed into the one shape a Save writes, so an opened record signs as saved.
        answerEdits: parseAnswerEdits(state?.answerEdits),
        conversion: parseConversion(state?.conversion),
        // M4S-5777 (X22): absent or unknown reads as completes.
        respondentSet: respondentSetOf(state?.respondentSet),
        // M4S-5777 X7: null is a crosstab saved before study types were saved, not a default.
        studyType: parseStudyTypeRecord(state?.studyType),
        // M4S-5777 X41: null when the crosstab has no filter tabs.
        filterTabs: parseFilterTabs(state?.filterTabs),
        // M4S-5777 (X105): null when the crosstab never chose an export preset.
        exportPreset: parseExportPresetId(state?.exportPreset),
        privacyFloor: parsePrivacyFloor(state?.privacyFloor),
        // M4S-5777 (X101): null when the crosstab hides no answers.
        emptyAnswers: parseEmptyAnswers(state?.emptyAnswers),
        // M4S-5777 X150: null when the crosstab did not come from a DANI chat.
        daniOrigin: parseDaniOrigin(state?.daniOrigin),
    };
}
